import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Loader2, Mail, Lock, AtSign, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { YaprioLogo } from "@/components/YaprioLogo";

function GoogleButton({ onSuccess }) {
  const { googleLogin } = useAuth();
  const [loading, setLoading] = useState(false);

  const startGoogle = async () => {
    if (loading) return;
    setLoading(true);
    const res = await googleLogin();
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to Yaprio! 🎉");
      onSuccess?.();
    } else if (!res.cancelled) {
      toast.error(res.error || "Google sign-in failed. Please try again.");
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      onClick={startGoogle}
      disabled={loading}
      data-testid="btn-google-login"
      className="w-full h-11 rounded-full border-slate-200 font-semibold gap-2 hover:bg-slate-50"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
          <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5c-7.6 0-14.2 4.3-17.7 10.2z"/>
          <path fill="#4CAF50" d="M24 43.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C10 39.1 16.5 43.5 24 43.5z"/>
          <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.2 5.2c-.4.4 6.6-4.8 6.6-14.6 0-1.2-.1-2.3-.4-3.5z"/>
        </svg>
      )}
      {loading ? "Signing in..." : "Continue with Google"}
    </Button>
  );
}

export default function AuthModal({ open, onOpenChange, initialMode = "signup" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [availability, setAvailability] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => setMode(initialMode), [initialMode, open]);

  useEffect(() => {
    if (mode !== "signup" || !username) {
      setAvailability(null);
      return;
    }
    setAvailability("checking");
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/check-username", { params: { username } });
        setAvailability(data);
      } catch {
        setAvailability(null);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [username, mode]);

  const reset = () => {
    setEmail(""); setUsername(""); setPassword(""); setError(""); setAvailability(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "forgot") {
      setSubmitting(true);
      try {
        await api.post("/auth/forgot-password", { email: email.trim() });
        setMode("forgot-sent");
      } catch (e) {
        setError(e.response?.data?.detail || "We couldn't send the reset email. Please try again.");
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(true);
    const res = mode === "signup"
      ? await register(email.trim(), username.trim(), password)
      : await login(email.trim(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(mode === "signup" ? "Welcome to Yaprio! 🎉" : "Welcome back!");
      onOpenChange(false);
      reset();
    } else setError(res.error);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl border-slate-100 p-0 overflow-hidden" data-testid="auth-modal">
        <DialogTitle className="sr-only">{mode === "signup" ? "Create your Yaprio account" : "Log in to Yaprio"}</DialogTitle>
        <DialogDescription className="sr-only">Sign up or log in with email and a unique username, or continue with Google.</DialogDescription>
        <div className="p-7 sm:p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <YaprioLogo size={40} subtitle={false} />
            <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-slate-900">
              {mode === "signup" && "Create your account"}
              {mode === "login" && "Welcome back"}
              {mode === "forgot" && "Reset your password"}
              {mode === "forgot-sent" && "Check your inbox"}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {mode === "signup" && "Pick a unique @username and start connecting."}
              {mode === "login" && "Log in to continue."}
              {mode === "forgot" && "We'll email you a link to choose a new password."}
              {mode === "forgot-sent" && "If that account exists, a reset link is on its way."}
            </p>
          </div>

          {mode === "forgot" ? (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Email</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"/><Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" data-testid="input-forgot-email" className="pl-9 h-11 rounded-xl"/></div></div>
              <Button type="submit" disabled={submitting} data-testid="btn-forgot-submit" className="w-full h-11 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-semibold">{submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : "Send reset link"}</Button>
              <button type="button" onClick={() => {setMode("login");setError("");}} data-testid="btn-back-to-login" className="flex w-full items-center justify-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900"><ArrowLeft className="h-3.5 w-3.5"/> Back to login</button>
            </form>
          ) : mode === "forgot-sent" ? (
            <div className="space-y-4 text-center" data-testid="forgot-sent"><p className="text-sm text-slate-600">If an account exists for that email, we've sent a reset link. It expires in 30 minutes.</p><Button onClick={() => setMode("login")} data-testid="btn-forgot-done" className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-semibold">Back to login</Button></div>
          ) : (
            <>
              <GoogleButton onSuccess={() => { onOpenChange(false); reset(); }} />
              <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-slate-100"/><span className="text-xs text-slate-400 font-medium">or with email</span><div className="h-px flex-1 bg-slate-100"/></div>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Email</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"/><Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" data-testid="input-auth-email" className="pl-9 h-11 rounded-xl"/></div></div>
                {mode === "signup" && <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Username</Label><div className="relative"><AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"/><Input required value={username} onChange={e => setUsername(e.target.value)} placeholder="yourname" data-testid="input-auth-username" className="pl-9 pr-9 h-11 rounded-xl"/><span className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="username-availability-indicator">{availability === "checking" && <Loader2 className="h-4 w-4 text-slate-400 animate-spin"/>}{availability?.available && <CheckCircle2 className="h-4 w-4 text-green-500"/>}{availability && availability !== "checking" && !availability.available && <XCircle className="h-4 w-4 text-red-500"/>}</span></div>{availability && availability !== "checking" && <p className={`text-xs font-medium ${availability.available ? "text-green-600" : "text-red-500"}`}>{availability.available ? "Nice — that username is available!" : availability.reason}</p>}</div>}
                <div className="space-y-1.5"><div className="flex items-center justify-between"><Label className="text-xs font-semibold text-slate-600">Password</Label>{mode === "login" && <button type="button" onClick={() => {setMode("forgot");setError("");}} data-testid="btn-forgot-password" className="text-xs font-semibold text-pink-600 hover:text-pink-700">Forgot password?</button>}</div><div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400"/><Input type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder={mode === "signup" ? "At least 8 characters" : "Your password"} data-testid="input-auth-password" className="pl-9 h-11 rounded-xl"/></div></div>
                {error && <p className="text-sm text-red-500 font-medium" data-testid="auth-error">{error}</p>}
                <Button type="submit" disabled={submitting || (mode === "signup" && availability && availability !== "checking" && !availability.available)} data-testid="btn-auth-submit" className="w-full h-11 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-semibold shadow-lg shadow-pink-500/30">{submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : mode === "signup" ? "Create account" : "Log in"}</Button>
              </form>
              <p className="text-center text-sm text-slate-500 mt-5">{mode === "signup" ? "Already have an account?" : "New to Yaprio?"}{" "}<button type="button" onClick={() => {setMode(mode === "signup" ? "login" : "signup");setError("");}} className="font-semibold text-pink-600 hover:text-pink-700" data-testid="btn-toggle-auth-mode">{mode === "signup" ? "Log in" : "Sign up"}</button></p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
